// features/search/searchSlice.js
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  // Search parameters
  searchParams: {
    query: "",
    location: "",
  },

  // Filters
  filters: {
    minPrice: 1000,
    maxPrice: 10000,
    // NEW PG FIELDS
    sharingType: "", // 1 (Private), 2 (Double), etc.
    bathroomType: "", // "attached" or "common"
    gender: "", // "male", "female", "coed"
    tenantType: "", // "Student", "Professional"

    // bedrooms: 0,
    // bathrooms: 0,
    // propertyType: "",
    amenities: [],
  },

  // UI state
  filtersOpen: false,
  searchHistory: [],
  recentSearches: [],

  // Results
  searchResults: null,
  totalResults: 0,
  currentPage: 1,
  totalPages: 1,

  // Loading/Error states
  isLoading: false,
  error: null,

  // Map state
  mapView: {
    center: [25.2048, 55.2708], // Default to Dubai
    zoom: 12,
    bounds: null,
  },
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {
    // Search query actions
    setSearchQuery: (state, action) => {
      state.searchParams.query = action.payload;
    },

    setSearchLocation: (state, action) => {
      state.searchParams.location = action.payload;
    },

    // Filter actions
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },

    setFilterValue: (state, action) => {
      const { field, value } = action.payload;
      state.filters[field] = value;
    },

    updateNumberFilter: (state, action) => {
      const { field, delta } = action.payload;
      state.filters[field] = Math.max(0, (state.filters[field] || 0) + delta);
    },

    toggleAmenity: (state, action) => {
      const amenity = action.payload;
      if (state.filters.amenities.includes(amenity)) {
        state.filters.amenities = state.filters.amenities.filter(
          (a) => a !== amenity
        );
      } else {
        state.filters.amenities.push(amenity);
      }
    },

    clearAllFilters: (state) => {
      state.filters = initialState.filters;
    },

    // UI actions
    setFiltersOpen: (state, action) => {
      state.filtersOpen = action.payload;
    },

    // Results actions
    setSearchResults: (state, action) => {
      state.searchResults = action.payload.results;
      state.totalResults =
        action.payload.total || action.payload.results?.length || 0;
      state.currentPage = action.payload.page || 1;
      state.totalPages = action.payload.totalPages || 1;
    },

    clearSearchResults: (state) => {
      state.searchResults = null;
      state.totalResults = 0;
      state.currentPage = 1;
      state.totalPages = 1;
    },

    // Loading/Error actions
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },

    setError: (state, action) => {
      state.error = action.payload;
    },

    // Map actions
    setMapView: (state, action) => {
      state.mapView = { ...state.mapView, ...action.payload };
    },

    setMapBounds: (state, action) => {
      state.mapView.bounds = action.payload;
    },

    // History actions
    addToSearchHistory: (state, action) => {
      const search = {
        ...action.payload,
        timestamp: new Date().toISOString(),
      };

      // Remove duplicates
      state.searchHistory = state.searchHistory.filter(
        (s) =>
          s.query !== search.query ||
          JSON.stringify(s.filters) !== JSON.stringify(search.filters)
      );

      // Add to beginning and limit to 20
      state.searchHistory.unshift(search);
      if (state.searchHistory.length > 20) {
        state.searchHistory.pop();
      }
    },

    addToRecentSearches: (state, action) => {
      const search = action.payload;

      // Remove if already exists
      state.recentSearches = state.recentSearches.filter(
        (s) => s.query !== search.query
      );

      // Add to beginning and limit to 5
      state.recentSearches.unshift(search);
      if (state.recentSearches.length > 5) {
        state.recentSearches.pop();
      }
    },

    clearSearchHistory: (state) => {
      state.searchHistory = [];
    },

    // Reset actions
    resetSearch: () => initialState,

    clearRecentSearches: (state) => {
      state.recentSearches = [];
    },

    removeRecentSearch: (state, action) => {
      const index = action.payload;
      if (index >= 0 && index < state.recentSearches.length) {
        state.recentSearches.splice(index, 1);
      }
    },

    removeRecentSearchByQuery: (state, action) => {
      const query = action.payload;
      state.recentSearches = state.recentSearches.filter(
        (search) => search.query !== query
      );
    },
  },
});

// Export actions
export const {
  setSearchQuery,
  setSearchLocation,
  setFilters,
  setFilterValue,
  updateNumberFilter,
  toggleAmenity,
  clearAllFilters,
  setFiltersOpen,
  setSearchResults,
  clearSearchResults,
  setLoading,
  setError,
  setMapView,
  setMapBounds,
  addToSearchHistory,
  addToRecentSearches,
  clearSearchHistory,
  resetSearch,
  clearRecentSearches,
  removeRecentSearch,
  removeRecentSearchByQuery,
} = searchSlice.actions;

// Selectors
export const selectSearchQuery = (state) => state.search.searchParams.query;
export const selectSearchLocation = (state) =>
  state.search.searchParams.location;
export const selectFilters = (state) => state.search.filters;
export const selectFiltersOpen = (state) => state.search.filtersOpen;
export const selectSearchResults = (state) => state.search.searchResults;
export const selectTotalResults = (state) => state.search.totalResults;
export const selectCurrentPage = (state) => state.search.currentPage;
export const selectTotalPages = (state) => state.search.totalPages;
export const selectIsLoading = (state) => state.search.isLoading;
export const selectSearchError = (state) => state.search.error;
export const selectMapView = (state) => state.search.mapView;
export const selectSearchHistory = (state) => state.search.searchHistory;
export const selectRecentSearches = (state) => state.search.recentSearches;

// Combined selector for all search params
export const selectAllSearchParams = (state) => ({
  ...state.search.searchParams,
  ...state.search.filters,
});

export default searchSlice.reducer;
